/**
 * GlobalGoalTrackingPage — operator UI for network-level goal tracking
 * (feature FS-302). Trackier-style CRUD list.
 *
 * Composition:
 *   - Topbar        : title + primary "Create goal" button
 *   - Filter bar    : free-text search + metric-type filter
 *   - Table         : name, type, target value, tracking % (elapsed period),
 *                     status — with loading / empty / error states
 *   - Create drawer : slide-over with zod-validated RHF form
 *                     (name, type, target value, payout — payout folded
 *                     into target_value cents when type=revenue)
 *
 * Styling:
 *   - Green accent only (green-600 / green-700 / green-50).
 *   - White background, zinc-200 borders, zinc-900 text, Inter (inherited).
 *   - 6-8 px rounded corners.
 *   - Inline SVG icons — no emoji, no dark theme, no competitor names.
 */
import {
	useMemo,
	useState,
	type FormEvent,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
	elapsedPct,
	formatTarget,
	GOAL_METRICS,
	MAX_TARGET_VALUE,
	metricLabel,
	NAME_MAX,
	NAME_MIN,
	statusLabel,
	type NetworkGoalMetric,
	type NetworkGoalView,
} from "./api";
import {
	useGlobalGoalTrackingCreate,
	useGlobalGoalTrackingDelete,
	useGlobalGoalTrackingList,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — green accent only.
// ---------------------------------------------------------------------------

const SHELL = "min-h-screen w-full bg-white text-zinc-900";
const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const SELECT = INPUT;
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-600 bg-green-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 hover:border-red-300 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Inline icons.
// ---------------------------------------------------------------------------

function PlusIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			fill="none"
			className="h-4 w-4"
		>
			<path
				d="M10 4v12M4 10h12"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
			/>
		</svg>
	);
}

function SearchIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			fill="none"
			className="h-4 w-4 text-zinc-400"
		>
			<circle
				cx="9"
				cy="9"
				r="5"
				stroke="currentColor"
				strokeWidth="1.75"
			/>
			<path
				d="m16 16-3.5-3.5"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
			/>
		</svg>
	);
}

function CloseIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			fill="none"
			className="h-4 w-4"
		>
			<path
				d="m5 5 10 10M15 5 5 15"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
			/>
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Create-goal form schema.
// ---------------------------------------------------------------------------

const createGoalSchema = z
	.object({
		name: z
			.string()
			.trim()
			.min(NAME_MIN, "Name is required")
			.max(NAME_MAX, `Name must be ${NAME_MAX} characters or fewer`),
		type: z.enum(GOAL_METRICS),
		// `target` is the human-entered figure: dollars for revenue, units
		// for conversions. We normalize to cents / integer units before POST.
		target: z
			.number({
				error: "Target value is required",
			})
			.finite()
			.nonnegative("Target cannot be negative"),
		payout: z
			.number({
				error: "Payout is required",
			})
			.finite()
			.nonnegative("Payout cannot be negative"),
	})
	.refine(
		(v) => {
			if (v.type === "revenue_cents") {
				return Math.round(v.target * 100) <= MAX_TARGET_VALUE;
			}
			return Math.floor(v.target) <= MAX_TARGET_VALUE;
		},
		{
			message: "Target value is too large",
			path: ["target"],
		},
	);

type CreateGoalFormValues = z.infer<typeof createGoalSchema>;

// ---------------------------------------------------------------------------
// Component.
// ---------------------------------------------------------------------------

export function GlobalGoalTrackingPage() {
	const listQuery = useGlobalGoalTrackingList();
	const createMut = useGlobalGoalTrackingCreate();
	const deleteMut = useGlobalGoalTrackingDelete();

	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<"all" | NetworkGoalMetric>(
		"all",
	);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const goals = listQuery.data?.goals ?? [];
	const filteredGoals = useMemo(() => {
		const needle = search.trim().toLowerCase();
		return goals.filter((g) => {
			if (typeFilter !== "all" && g.metric !== typeFilter) return false;
			if (needle && !g.name.toLowerCase().includes(needle)) return false;
			return true;
		});
	}, [goals, search, typeFilter]);

	const nowMs = Date.now();

	const openDrawer = () => setDrawerOpen(true);
	const closeDrawer = () => setDrawerOpen(false);

	const handleDelete = (goal: NetworkGoalView) => {
		if (
			typeof window !== "undefined" &&
			!window.confirm(`Delete goal "${goal.name}"?`)
		) {
			return;
		}
		deleteMut.mutate(goal.id);
	};

	return (
		<main
			data-testid="global-goal-tracking-page"
			className={SHELL}
		>
			<div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8">
				{/* --- Topbar ----------------------------------------------------- */}
				<header
					className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
					data-testid="global-goal-tracking-topbar"
				>
					<div className="flex flex-col gap-1">
						<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
							Network goals
						</p>
						<h1 className="text-2xl font-semibold text-zinc-900">
							Global Goal Tracking
						</h1>
						<p className={HINT}>
							Track network-wide revenue and conversion targets across
							campaigns and affiliates.
						</p>
					</div>
					<div>
						<button
							type="button"
							className={BTN_PRIMARY}
							onClick={openDrawer}
							data-testid="global-goal-tracking-create-goal-btn"
						>
							<PlusIcon />
							Create goal
						</button>
					</div>
				</header>

				{/* --- Filter bar ------------------------------------------------- */}
				<section
					className={`${CARD} flex flex-col gap-3 p-4 sm:flex-row sm:items-end`}
					data-testid="global-goal-tracking-filter-bar"
				>
					<div className="flex-1">
						<label className={LABEL} htmlFor="ggt-search">
							Search
						</label>
						<div className="relative mt-1">
							<span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
								<SearchIcon />
							</span>
							<input
								id="ggt-search"
								type="search"
								className={`${INPUT} pl-9`}
								placeholder="Search by goal name"
								value={search}
								onChange={(e) => setSearch(e.currentTarget.value)}
								data-testid="global-goal-tracking-search-input"
								autoComplete="off"
							/>
						</div>
					</div>
					<div className="w-full sm:w-48">
						<label className={LABEL} htmlFor="ggt-type">
							Type
						</label>
						<select
							id="ggt-type"
							className={`${SELECT} mt-1`}
							value={typeFilter}
							onChange={(e) =>
								setTypeFilter(
									e.currentTarget.value as "all" | NetworkGoalMetric,
								)
							}
							data-testid="global-goal-tracking-type-filter"
						>
							<option value="all">All types</option>
							{GOAL_METRICS.map((m) => (
								<option key={m} value={m}>
									{metricLabel(m)}
								</option>
							))}
						</select>
					</div>
				</section>

				{/* --- Table ------------------------------------------------------ */}
				<section
					className={`${CARD} overflow-hidden`}
					data-testid="global-goal-tracking-table-section"
					aria-label="Goals list"
				>
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-200 text-left text-sm">
							<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wider text-zinc-500">
								<tr>
									<th scope="col" className="px-4 py-3">
										Name
									</th>
									<th scope="col" className="px-4 py-3">
										Type
									</th>
									<th scope="col" className="px-4 py-3">
										Target
									</th>
									<th scope="col" className="px-4 py-3 w-40">
										Tracking
									</th>
									<th scope="col" className="px-4 py-3">
										Status
									</th>
									<th scope="col" className="px-4 py-3 text-right">
										<span className="sr-only">Actions</span>
									</th>
								</tr>
							</thead>
							<tbody
								className="divide-y divide-zinc-100 bg-white"
								data-testid="global-goal-tracking-table-body"
							>
								{listQuery.isLoading ? (
									<tr>
										<td
											colSpan={6}
											className="px-4 py-10 text-center text-sm text-zinc-500"
											data-testid="global-goal-tracking-loading"
										>
											Loading goals...
										</td>
									</tr>
								) : listQuery.isError ? (
									<tr>
										<td
											colSpan={6}
											className="px-4 py-10 text-center text-sm text-red-600"
											role="alert"
											data-testid="global-goal-tracking-error"
										>
											{listQuery.error?.message ?? "Failed to load goals."}
										</td>
									</tr>
								) : filteredGoals.length === 0 ? (
									<tr>
										<td
											colSpan={6}
											className="px-4 py-10 text-center text-sm text-zinc-500"
											data-testid="global-goal-tracking-empty"
										>
											{goals.length === 0
												? "No goals yet. Create your first network goal."
												: "No goals match your filters."}
										</td>
									</tr>
								) : (
									filteredGoals.map((goal) => {
										const trackingPct = elapsedPct(goal, nowMs);
										const statusText = statusLabel(goal);
										return (
											<tr
												key={goal.id}
												data-testid={`global-goal-tracking-row-${goal.id}`}
											>
												<td className="whitespace-nowrap px-4 py-3">
													<span className="font-medium text-zinc-900">
														{goal.name}
													</span>
												</td>
												<td className="whitespace-nowrap px-4 py-3 text-zinc-700">
													{metricLabel(goal.metric)}
												</td>
												<td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-zinc-700">
													{formatTarget(goal)}
												</td>
												<td className="whitespace-nowrap px-4 py-3">
													<div className="flex items-center gap-2">
														<div
															className="h-1.5 w-24 overflow-hidden rounded-full bg-zinc-100"
															aria-hidden="true"
														>
															<div
																className="h-full rounded-full bg-green-600"
																style={{
																	width: `${Math.min(
																		100,
																		Math.max(0, trackingPct),
																	).toFixed(1)}%`,
																}}
															/>
														</div>
														<span className="text-xs tabular-nums text-zinc-600">
															{Math.min(
																100,
																Math.max(0, trackingPct),
															).toFixed(0)}
															%
														</span>
													</div>
												</td>
												<td className="whitespace-nowrap px-4 py-3">
													<span
														className={
															goal.active
																? "inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
																: "inline-flex rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600"
														}
													>
														{statusText}
													</span>
												</td>
												<td className="whitespace-nowrap px-4 py-3 text-right">
													<button
														type="button"
														className={BTN_DANGER}
														onClick={() => handleDelete(goal)}
														disabled={
															deleteMut.isPending &&
															deleteMut.variables === goal.id
														}
														data-testid={`global-goal-tracking-delete-${goal.id}`}
													>
														Delete
													</button>
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</section>
			</div>

			{/* --- Create drawer -------------------------------------------------- */}
			{drawerOpen ? (
				<CreateGoalDrawer
					onClose={closeDrawer}
					pending={createMut.isPending}
					error={createMut.error?.message ?? null}
					onSubmit={async (values) => {
						const targetValue =
							values.type === "revenue_cents"
								? Math.round(values.target * 100)
								: Math.max(0, Math.floor(values.target));
						const nowEpoch = Date.now();
						// Default 30-day window starting now — operators can edit later
						// via PATCH once that's surfaced.
						const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
						await createMut.mutateAsync({
							name: values.name,
							metric: values.type,
							target_value: targetValue,
							period_start_at: nowEpoch,
							period_end_at: nowEpoch + THIRTY_DAYS_MS,
							active: true,
						});
						closeDrawer();
					}}
				/>
			) : null}
		</main>
	);
}

// ---------------------------------------------------------------------------
// Create-goal drawer (slide-over panel + backdrop).
// ---------------------------------------------------------------------------

type CreateGoalDrawerProps = {
	onClose: () => void;
	pending: boolean;
	error: string | null;
	onSubmit: (values: CreateGoalFormValues) => Promise<void>;
};

function CreateGoalDrawer({
	onClose,
	pending,
	error,
	onSubmit,
}: CreateGoalDrawerProps) {
	const {
		register,
		handleSubmit,
		formState: { errors },
		watch,
	} = useForm<CreateGoalFormValues>({
		resolver: zodResolver(createGoalSchema),
		defaultValues: {
			name: "",
			type: "revenue_cents",
			target: 0,
			payout: 0,
		},
	});

	const currentType = watch("type");

	const submit = (e: FormEvent) => {
		e.preventDefault();
		void handleSubmit(async (values) => {
			try {
				await onSubmit(values);
			} catch {
				// Mutation error surfaces via the `error` prop.
			}
		})(e);
	};

	return (
		<div
			className="fixed inset-0 z-40 flex justify-end"
			data-testid="global-goal-tracking-drawer"
			role="dialog"
			aria-modal="true"
			aria-labelledby="global-goal-tracking-drawer-title"
		>
			<button
				type="button"
				aria-label="Close drawer"
				className="absolute inset-0 bg-zinc-900/30"
				onClick={onClose}
			/>
			<aside className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl">
				<header className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
					<h2
						id="global-goal-tracking-drawer-title"
						className="text-base font-semibold text-zinc-900"
					>
						Create goal
					</h2>
					<button
						type="button"
						className="rounded-[6px] p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
						onClick={onClose}
						aria-label="Close"
					>
						<CloseIcon />
					</button>
				</header>
				<form
					className="flex-1 overflow-y-auto px-5 py-5"
					onSubmit={submit}
					data-testid="global-goal-tracking-create-form"
				>
					<div className="flex flex-col gap-4">
						<div>
							<label className={LABEL} htmlFor="ggt-drawer-name">
								Name
							</label>
							<input
								id="ggt-drawer-name"
								className={`${INPUT} mt-1`}
								placeholder="Q2 Total Revenue"
								autoComplete="off"
								data-testid="global-goal-tracking-name-input"
								{...register("name")}
							/>
							{errors.name ? (
								<p className={`${ERROR_TEXT} mt-1`} role="alert">
									{errors.name.message}
								</p>
							) : null}
						</div>
						<div>
							<label className={LABEL} htmlFor="ggt-drawer-type">
								Type
							</label>
							<select
								id="ggt-drawer-type"
								className={`${SELECT} mt-1`}
								data-testid="global-goal-tracking-type-input"
								{...register("type")}
							>
								{GOAL_METRICS.map((m) => (
									<option key={m} value={m}>
										{metricLabel(m)}
									</option>
								))}
							</select>
							{errors.type ? (
								<p className={`${ERROR_TEXT} mt-1`} role="alert">
									{errors.type.message}
								</p>
							) : null}
						</div>
						<div>
							<label className={LABEL} htmlFor="ggt-drawer-target">
								Target value
							</label>
							<input
								id="ggt-drawer-target"
								type="number"
								inputMode="decimal"
								min={0}
								step={currentType === "revenue_cents" ? "0.01" : "1"}
								className={`${INPUT} mt-1`}
								placeholder={
									currentType === "revenue_cents" ? "5000000" : "1000"
								}
								data-testid="global-goal-tracking-target-input"
								{...register("target", { valueAsNumber: true })}
							/>
							<p className={`${HINT} mt-1`}>
								{currentType === "revenue_cents"
									? "Dollars. Stored on the server as integer cents."
									: "Conversion count."}
							</p>
							{errors.target ? (
								<p className={`${ERROR_TEXT} mt-1`} role="alert">
									{errors.target.message}
								</p>
							) : null}
						</div>
						<div>
							<label className={LABEL} htmlFor="ggt-drawer-payout">
								Payout per conversion
							</label>
							<input
								id="ggt-drawer-payout"
								type="number"
								inputMode="decimal"
								min={0}
								step="0.01"
								className={`${INPUT} mt-1`}
								placeholder="0.00"
								data-testid="global-goal-tracking-payout-input"
								{...register("payout", { valueAsNumber: true })}
							/>
							<p className={`${HINT} mt-1`}>
								Shown on reports. Not yet sent to the server (tracked
								client-side for this release).
							</p>
							{errors.payout ? (
								<p className={`${ERROR_TEXT} mt-1`} role="alert">
									{errors.payout.message}
								</p>
							) : null}
						</div>
						{error ? (
							<p className={ERROR_TEXT} role="alert">
								{error}
							</p>
						) : null}
					</div>
					<footer className="mt-6 flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={onClose}
							disabled={pending}
						>
							Cancel
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={pending}
							data-testid="global-goal-tracking-submit"
						>
							{pending ? "Saving..." : "Create goal"}
						</button>
					</footer>
				</form>
			</aside>
		</div>
	);
}

export default GlobalGoalTrackingPage;
